import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiMic, FiMicOff, FiVideo, FiVideoOff, FiUsers, FiArrowRight } from 'react-icons/fi';
import Loader from 'pages/Test/localComponent/loader';
import ParticipantsPanel, { initialsOf } from './participantsPanel';
import useMicLevel from '../useMicLevel';
import '../broadcastNext.css';

const Clock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 20000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="bn-clock">
      {now.getHours().toString().padStart(2, '0')}:{now.getMinutes().toString().padStart(2, '0')}
    </div>
  );
};

/**
 * The screen a teacher lands on when they hit "Join now" on the ongoing class card.
 * Nothing here touches the media room — the mediasoup client is only created once
 * "Start class" is pressed and the live classroom mounts.
 */
const PreJoin = ({
  subjectInitial,
  subjectIcon,
  title,
  subtitle = 'Waiting to start',
  userName,
  timeRange,
  invitedCount,
  waitingCount,
  present = [],
  absent = [],
  isStudent = false,
  // Set once the teacher has ended a class that is still inside its scheduled slot:
  // the same screen, but the primary action is now going back in rather than starting.
  rejoin = false,
  starting = false,
  onMessage,
  chatSlot = null,
  panelTab,
  onPanelTabChange,
  // The lobby owns open/closed as well as the tab: what is on screen is what decides
  // whether an arriving message gets announced, and that decision is made up there.
  panelOpen = false,
  onPanelOpenChange = () => {},
  chatUnread = 0,
  recording = false,
  recordingNote = '',
  othersLive = false,
  onRecordingToggle,
  onStart,
  onLeave,
}) => {
  // The badge on the Participants button counts students who are here now — not the invited
  // roster it used to show, and not every connection either. A co-host, an admin sitting in
  // or a second teacher is in the list but is not the class, and the number the teacher is
  // reading before pressing Start is how many of their students have turned up. The list
  // still shows everyone; only the count is narrowed. `role` is written by roleOf, the same
  // rule that labels each row, so the badge and the list cannot disagree.
  const presentStudentCount = present.filter((person) => person.role === 'Student').length;
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [deviceError, setDeviceError] = useState(null);
  // The button is labelled Participants, so it opens on Participants. Neither half of the
  // panel state is ours - the lobby owns the tab (the Chat tab has to survive a row's
  // "message" click) and open/closed with it - but closing used to leave the tab wherever
  // it was last put, so after one look at Chat every later press of Participants reopened
  // the panel on the chat instead of the roster. Closing is left alone: the tab is only
  // reset on the way in, which is the moment the button's own label says what should be
  // on screen.
  const togglePanel = () => {
    if (panelOpen) {
      onPanelOpenChange(false);
      return;
    }
    if (panelTab !== 'participants') onPanelTabChange?.('participants');
    onPanelOpenChange(true);
  };
  // The chip's bars used to be a keyframe loop here, bouncing at a fixed rate whether or not
  // anyone was talking. The preview stream is right there and already carries the mic, so
  // measure it: the lobby meter now says the same thing the one in class does.
  const micRootRef = useRef(null);
  useMicLevel(micRootRef, () => streamRef.current, micOn && !isStudent);

  const releaseDevices = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // Students never publish in Broadcast mode, so don't ask them for camera/mic access.
  useEffect(() => {
    if (isStudent) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (error) {
        if (!cancelled) {
          setDeviceError(
            error?.name === 'NotAllowedError'
              ? 'Camera and mic are blocked. Allow access to see your preview.'
              : 'No camera found. You can still start the class.',
          );
          setCamOn(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      releaseDevices();
    };
  }, [releaseDevices, isStudent]);

  const toggleMic = () => {
    const next = !micOn;
    streamRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = next;
    });
    setMicOn(next);
  };

  // Turning the camera off releases the device rather than just disabling the track —
  // otherwise the camera light stays on while the preview shows "Camera is off".
  const toggleCam = async () => {
    if (camOn) {
      streamRef.current?.getVideoTracks().forEach((track) => {
        track.stop();
        streamRef.current.removeTrack(track);
      });
      if (videoRef.current) {
        videoRef.current.srcObject = streamRef.current ?? null;
      }
      // The mic goes with it. With the camera off there is no Mic button on this screen, so
      // a mic left on would be one nobody could turn off — and the name chip would show
      // level bars beside a control that is not there.
      streamRef.current?.getAudioTracks().forEach((track) => {
        track.enabled = false;
      });
      setMicOn(false);
      setCamOn(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const track = stream.getVideoTracks()[0];
      if (streamRef.current) {
        streamRef.current.addTrack(track);
      } else {
        streamRef.current = stream;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = streamRef.current;
      }
      // And the mic comes back with it. A camera on with no sound is not a state anyone
      // means to be in, and it is the same rule the class follows: a picture on the wire
      // brings the mic with it. The Mic button is back on screen at the same moment, so
      // turning it off again is one press away.
      streamRef.current?.getAudioTracks().forEach((track) => {
        track.enabled = true;
      });
      setMicOn(true);
      setCamOn(true);
    } catch (error) {
      setDeviceError('Camera could not be started. You can still start the class.');
    }
  };

  // Another teacher is already publishing, and the room holds one publisher at a time. Mic,
  // camera and Record here are all choices about going live, so none of them can be honoured
  // yet - joining is still fine, and they become available once that class stops.
  useEffect(() => {
    if (!othersLive) return;
    setMicOn(false);
    setCamOn(false);
    if (recording) onRecordingToggle?.(false);
    // Releasing here too: a preview already running would otherwise keep the camera light on
    // over a screen that no longer offers to use it.
    releaseDevices();
    // recording and onRecordingToggle are deliberately not deps: they are read to undo a
    // choice already made, and listing them would re-run this on every toggle.
  }, [othersLive, releaseDevices]);

  const handleStart = () => {
    // Ahead of releaseDevices, not after it. The route wrapper refuses this press while the
    // browser reports no connection, and a refusal has to leave the screen exactly as it
    // found it — releasing first would kill the camera preview on the way to a class that
    // never starts, and nothing here turns it back on.
    if (navigator.onLine === false) {
      onStart(null);
      return;
    }
    // Free the preview devices before the media client asks for them.
    releaseDevices();
    // Never carry a device or a recording into a room that cannot publish yet: the room's own
    // guard would refuse the publish, and startPrefs would still be asking for it.
    // `camOn && micOn` rather than plain micOn: the camera can also go off without anyone
    // pressing its button (no device, permission refused), and that path has no Mic button
    // on screen either. Whatever put the camera off, the mic does not travel into the class
    // on its own — there would be no control for it there.
    onStart(othersLive
      ? { micOn: false, camOn: false, recording: false }
      : { micOn: camOn && micOn, camOn, recording });
  };

  const hasPreview = camOn && !deviceError && !isStudent && !othersLive;
  const placeholderNote = isStudent
    ? 'The class starts when your teacher goes live.'
    : deviceError || 'Camera is off';

  return (
    <div className="bn-root" ref={micRootRef}>
      <div className="bn-header">
        <div className="bn-header-left">
          {/* The badge names the class, so it is the subject's own icon - the same one the
              schedule card shows - falling back to the subject's initial when no icon has
              been uploaded. This used to be the institute logo, which is identical for every
              class and so said nothing about which one you had joined. */}
          <div className="bn-org">{subjectIcon ? <img src={subjectIcon} alt="" /> : subjectInitial}</div>
          <div style={{ minWidth: 0 }}>
            <div className="bn-title">{title}</div>
            <div className="bn-subtitle">{subtitle}</div>
          </div>
        </div>
        <div className="bn-header-right">
          <Clock />
        </div>
      </div>

      <div className="bn-body">
        <div className="bn-prejoin-body">
          <div className="bn-prejoin-inner">
            <div className="bn-preview-col">
              <div className="bn-preview">
                <video
                  ref={videoRef}
                  className="bn-selfie"
                  autoPlay
                  playsInline
                  muted
                  style={{ display: hasPreview ? 'block' : 'none' }}
                />
                {!hasPreview ? (
                  <div className="bn-preview-placeholder">
                    <div className="bn-avatar-initials">{initialsOf(userName)}</div>
                    <div className="bn-preview-off">{placeholderNote}</div>
                  </div>
                ) : null}
                <div className="bn-name-chip">
                  <span>{userName} (You)</span>
                  {/* Same chip the live stage shows: level bars while the mic is on, the
                      muted tag while it is off. */}
                  {isStudent ? null : micOn ? (
                    <span className="bn-levels">
                      <i />
                      <i />
                      <i />
                      <i />
                      <i />
                    </span>
                  ) : (
                    <span className="bn-mute-tag">muted</span>
                  )}
                </div>
              </div>

              {!isStudent ? (
                <div className="bn-device-row">
                  {/* No camera, no Mic button — the same rule the class itself follows. */}
                  {camOn ? (
                    <button
                      type="button"
                      className={`bn-device-btn ${micOn ? 'bn-on' : ''}`}
                      onClick={toggleMic}
                      disabled={othersLive}
                      title={othersLive ? 'Another teacher is live in this class' : undefined}
                    >
                      {micOn ? <FiMic size={16} /> : <FiMicOff size={16} />}
                      {micOn ? 'Mic on' : 'Mic off'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={`bn-device-btn ${camOn ? 'bn-on' : ''}`}
                    onClick={toggleCam}
                    disabled={!!deviceError || othersLive}
                    title={othersLive ? 'Another teacher is live in this class' : undefined}
                  >
                    {camOn ? <FiVideo size={16} /> : <FiVideoOff size={16} />}
                    {camOn ? 'Camera on' : 'Camera off'}
                  </button>
                </div>
              ) : null}
            </div>

            <div className="bn-side">
              <div className="bn-eyebrow">{timeRange ? `Session · ${timeRange}` : 'Session'}</div>
              <h1 className="bn-heading">
                {isStudent ? 'Ready to join' : 'Ready when you are'}
                {userName ? `, ${userName.trim()}.` : '.'}
              </h1>
              <p className="bn-meta">
                {typeof invitedCount === 'number'
                  ? `${invitedCount} students invited`
                  : 'Class roster loading'}
                {typeof waitingCount === 'number' ? ` · ${waitingCount} already waiting` : ''}
              </p>

              {!isStudent ? (
                <div className="bn-card">
                  <div className="bn-card-main">
                    <span>Recording</span>
                    {/* The reason replaces the usual note: a switch that cannot move should
                        say why rather than leave the note that described what it would do. */}
                    {othersLive ? (
                      <span className="bn-card-note">Another teacher is live in this class</span>
                    ) : recordingNote ? (
                      <span className="bn-card-note">{recordingNote}</span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="bn-switch"
                    data-on={recording}
                    aria-pressed={recording}
                    aria-label="Record this class"
                    disabled={othersLive}
                    title={othersLive ? 'Another teacher is live in this class' : undefined}
                    onClick={() => onRecordingToggle(!recording)}
                  >
                    <span />
                  </button>
                </div>
              ) : null}

              <div className="bn-actions">
                <button
                  type="button"
                  className="bn-btn bn-btn-primary"
                  onClick={handleStart}
                  disabled={starting}
                >
                  {starting ? (
                    <Loader style={{ fontSize: 16, color: '#fff' }} />
                  ) : (
                    <>
                      {rejoin ? 'Re-join Class' : isStudent ? 'Join class' : 'Start class'}{' '}
                      <FiArrowRight size={16} />
                    </>
                  )}
                </button>

                <button type="button" className="bn-btn bn-btn-danger-soft" onClick={onLeave}>
                  Leave
                </button>

                <button
                  type="button"
                  className="bn-btn bn-btn-quiet"
                  onClick={togglePanel}
                >
                  <FiUsers size={16} /> Participants
                  <span className="bn-count-pill">{presentStudentCount}</span>
                  {/* With the panel shut this button is the whole chat: the toast that
                      announced the message is long gone by the time anyone looks up. */}
                  {!panelOpen && chatUnread > 0 ? <span className="bn-unread-dot" /> : null}
                </button>
              </div>
            </div>
          </div>
        </div>

        {panelOpen ? (
          <ParticipantsPanel
            present={present}
            absent={absent}
            onClose={() => onPanelOpenChange(false)}
            onMessage={onMessage}
            chatSlot={chatSlot}
            activeTab={panelTab}
            onTabChange={onPanelTabChange}
            chatUnread={chatUnread}
          />
        ) : null}
      </div>
    </div>
  );
};

export default PreJoin;
